import { createContext, useContext, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { apiClient, configureApiClient } from '../shared/lib/apiClient';
import { navigate } from '../shared/lib/router';

const AuthContext = createContext(null);

/**
 * Holds the authenticated session. The access token lives ONLY in React
 * state/memory (never localStorage/sessionStorage) to reduce the blast
 * radius of an XSS-style token exfiltration. The refresh token is an
 * httpOnly cookie set by the backend that this code never reads or writes
 * directly - refresh/login/logout calls just need `credentials: 'include'`,
 * which apiClient always sets.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [enabledModules, setEnabledModules] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [accessToken, setAccessToken] = useState(null);
  const [initializing, setInitializing] = useState(true);
  // The absolute instant (ISO string) the backend computed this session
  // will hit the tenant's configured session-timeout cap -- see
  // computeSessionExpiresAt in apps/api/src/lib/session.js. Drives the
  // auto-logout timer effect below.
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);

  // apiClient is a plain (non-React) module, so it needs a ref to always
  // read the *current* token synchronously instead of a stale closure.
  const accessTokenRef = useRef(null);
  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  const applySession = useCallback((session) => {
    setUser(session.user || null);
    setTenant(session.tenant || null);
    setEnabledModules(session.enabledModules || []);
    setPermissions(session.permissions || []);
    if (session.accessToken) {
      setAccessToken(session.accessToken);
    }
    if (session.sessionExpiresAt) {
      setSessionExpiresAt(session.sessionExpiresAt);
    }

    // TenantContext's guess (from the URL, defaulting to "demo" on plain
    // localhost) is only ever a PRE-login guess used to target the initial
    // login/refresh call. Once the backend confirms which tenant the
    // session actually belongs to, that must win for every subsequent
    // request -- otherwise a user who logs into a different workspace than
    // the URL/default guess (exactly what happened debugging this) keeps
    // sending the wrong X-Tenant-Slug header on every request after login
    // and gets TENANT_NOT_FOUND / 404 on everything.
    if (session.tenant?.slug) {
      configureApiClient({ getTenantSlug: () => session.tenant.slug });
    }
  }, []);

  const clearSession = useCallback(() => {
    setUser(null);
    setTenant(null);
    setEnabledModules([]);
    setPermissions([]);
    setAccessToken(null);
    setSessionExpiresAt(null);
  }, []);

  const refreshAccessToken = useCallback(async () => {
    const response = await apiClient.post('/auth/refresh');
    applySession(response.data);
    return response.data.accessToken;
  }, [applySession]);

  const login = useCallback(
    async (email, password, tenantSlug, turnstileToken) => {
      const response = await apiClient.post('/auth/login', {
        tenantSlug,
        email,
        password,
        turnstileToken,
      });
      applySession(response.data);
      return response.data;
    },
    [applySession]
  );

  // Completes an MFA-gated login: `path` is one of the /auth/mfa/login/*
  // endpoints (totp, backup-code, webauthn-verify). On success the response
  // has the exact same shape as a non-MFA login, so it applies the same way.
  const completeMfaChallenge = useCallback(
    async (path, body) => {
      const response = await apiClient.post(path, body);
      applySession(response.data);
      return response.data;
    },
    [applySession]
  );

  // Centralized so both a manual "Sign out" click (TopNav.jsx, after its
  // own confirmation step) and the auto-logout timer below share the exact
  // same clear-session-then-redirect behavior, rather than duplicating the
  // tenant-preserving navigate() logic in two places. `reason: 'expired'`
  // (auto-logout only) rides along as a query param so LoginPage can show
  // "your session has expired" instead of the plain sign-in screen.
  const logout = useCallback(
    async ({ reason } = {}) => {
      const tenantSlug = tenant?.slug;
      try {
        await apiClient.post('/auth/logout');
      } catch (e) {
        // Best-effort: even if the network call fails, drop the local session.
      } finally {
        clearSession();
      }
      const params = new URLSearchParams();
      if (tenantSlug) params.set('tenant', tenantSlug);
      if (reason) params.set('reason', reason);
      const query = params.toString();
      // logout() only ever runs while some authenticated page is mounted
      // (the deep path the user was last on, e.g. /customers) -- without an
      // explicit navigate, the URL bar (and a page reload) would stay there
      // even though LoginPage is what actually renders once isAuthenticated
      // flips false.
      navigate(query ? `/?${query}` : '/', { replace: true });
    },
    [clearSession, tenant]
  );

  // A hard ceiling on session length, enforced authoritatively server-side
  // (see /auth/refresh in apps/api/src/modules/core/routes.js -- a refresh
  // past this point is rejected regardless of what the frontend does). This
  // timer exists purely so the USER gets a clean, proactive redirect to the
  // login screen the moment their session lapses, instead of just seeing
  // random 401s the next time they happen to click something.
  useEffect(() => {
    if (!sessionExpiresAt) return undefined;

    const expiryMs = new Date(sessionExpiresAt).getTime();
    const msRemaining = expiryMs - Date.now();
    if (msRemaining <= 0) {
      logout({ reason: 'expired' });
      return undefined;
    }

    const timer = setTimeout(() => logout({ reason: 'expired' }), msRemaining);

    // A long-running setTimeout can be delayed (or effectively paused) by
    // a backgrounded/throttled or sleeping tab -- e.g. a laptop closed for
    // the whole timeout window. Re-checking the absolute deadline whenever
    // the tab becomes visible again catches that immediately, rather than
    // leaving an already-expired session looking "live" in the UI until
    // the next API call happens to 401.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && Date.now() >= expiryMs) {
        logout({ reason: 'expired' });
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [sessionExpiresAt, logout]);

  // Wire apiClient to this provider: it needs to read the current token
  // synchronously and to trigger a silent refresh+retry on 401.
  useEffect(() => {
    configureApiClient({
      getAccessToken: () => accessTokenRef.current,
      onUnauthorized: async () => {
        try {
          await refreshAccessToken();
          return true;
        } catch (e) {
          clearSession();
          return false;
        }
      },
    });
  }, [refreshAccessToken, clearSession]);

  // On first load, try to resume a session from the httpOnly refresh cookie
  // (e.g. after a page reload). Silently ignored if there is no valid cookie.
  useEffect(() => {
    let cancelled = false;
    refreshAccessToken()
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setInitializing(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasPermission = useCallback((permission) => permissions.includes(permission), [permissions]);
  const isModuleEnabled = useCallback((moduleKey) => enabledModules.includes(moduleKey), [enabledModules]);

  const value = useMemo(
    () => ({
      user,
      tenant,
      enabledModules,
      permissions,
      accessToken,
      sessionExpiresAt,
      isAuthenticated: Boolean(accessToken && user),
      initializing,
      login,
      completeMfaChallenge,
      logout,
      refreshAccessToken,
      hasPermission,
      isModuleEnabled,
      setUser,
    }),
    [
      user, tenant, enabledModules, permissions, accessToken, sessionExpiresAt, initializing,
      login, completeMfaChallenge, logout, refreshAccessToken, hasPermission, isModuleEnabled,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
